import {
    Alert,
    Badge,
    Box,
    FormControlLabel,
    IconButton,
    Menu,
    SvgIcon,
    Switch,
    TablePagination,
    Typography,
} from "@mui/material";
import { type ChangeEvent, useState } from "react";
import { Bell } from "react-feather";
import { USER_TABLE_LIMIT, USER_TABLE_PAGING_OPTIONS } from "src/constants";

import { useLinkButtonStyles } from "src/hooks/styles/useLinkButtonStyles";
import { NotificationElement } from "./NotificationElement";
import { useNotifications } from "./Notifications.hook";
import { useNotificationsModalContainerStyles } from "./Notifications.styles";

import { useTranslation } from "src/design-system/core/TranslationContext";
import { NOTIFICATIONS_TRANSLATION_NAMESPACE } from "./config";

export const NOTIFICATIONS_MODAL_CONTAINER_TK = "NOTIFICATIONS_MODAL_CONTAINER_TK";

export const NotificationsModalContainer = (): JSX.Element => {
    const { t } = useTranslation(
        `${NOTIFICATIONS_TRANSLATION_NAMESPACE}:${NOTIFICATIONS_MODAL_CONTAINER_TK}`
    );
    const classes = useNotificationsModalContainerStyles();
    const linkButtonClasses = useLinkButtonStyles();
    const {
        notifications,
        anchorEl,
        displayOnlyUnseenNotifications,
        handleOpen,
        handleClose,
        handleNotificationClick,
        handleSawNotifications,
        handleUnseenSwitch,
        disableAction,
    } = useNotifications();
    const newNotificationsNumber = notifications.filter((e) => !e.seenDateUTC).length;
    const [page, setPage] = useState<number>(0);
    const [limit, setLimit] = useState<number>(USER_TABLE_LIMIT);

    const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handlePageChange = (_event: any, newPage: number): void => {
        setPage(newPage);
    };

    return (
        <Box display="flex" alignItems="center">
            <IconButton color="primary" onClick={handleOpen}>
                <Badge badgeContent={newNotificationsNumber} color="error">
                    <SvgIcon>
                        <Bell />
                    </SvgIcon>
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                onKeyDown={disableAction}
                onKeyDownCapture={disableAction}
                classes={{ paper: classes.menu }}
            >
                <div className={classes.header}>
                    <Box px={3} py={2} display="flex" width="100%" justifyContent="space-between">
                        <Typography variant="subtitle" color="textPrimary">
                            {t("notifications")}
                        </Typography>
                        <FormControlLabel
                            labelPlacement="start"
                            control={
                                <Switch
                                    size="small"
                                    checked={displayOnlyUnseenNotifications}
                                    onChange={handleUnseenSwitch}
                                />
                            }
                            label={t("show-only-unread-notifications")}
                            sx={{
                                "& .MuiFormControlLabel-label": {
                                    fontSize: 12,
                                },
                            }}
                        />
                    </Box>
                </div>

                <Box px={3} py={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center">
                            <div className={classes.new} />
                            <Box ml={1} className={classes.smallText}>
                                {t("notification-unreadWithCount", {
                                    count: newNotificationsNumber,
                                })}
                            </Box>
                        </Box>

                        <Box
                            onClick={() => {
                                return handleSawNotifications(notifications);
                            }}
                            className={linkButtonClasses.link}
                        >
                            {t("all-mark-as-read")}
                        </Box>
                    </Box>
                </Box>

                {notifications
                    .slice(page * limit, page * limit + limit)
                    .map((notification, index) => (
                        <NotificationElement
                            key={index}
                            notification={notification}
                            onLinkClick={handleNotificationClick}
                        />
                    ))}
                {(!notifications || !notifications.length) && (
                    <Box px={2} py={1}>
                        <Alert severity="info" icon={false}>
                            {displayOnlyUnseenNotifications
                                ? t("no-notification-unread")
                                : t("no-notification-all-works")}
                        </Alert>
                    </Box>
                )}
                <Box display="flex" justifyContent="flex-end">
                    <TablePagination
                        count={notifications.length}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleLimitChange}
                        page={page}
                        rowsPerPage={limit}
                        rowsPerPageOptions={USER_TABLE_PAGING_OPTIONS}
                    />
                </Box>
            </Menu>
        </Box>
    );
};
